import { useEffect, useRef, useState } from "react";
import { FileCode2, FileText, ListChecks, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@ncoder/ui";
import { IconButton } from "@/components/atoms/multi-agent/Button";
import { cn } from "@/lib/utils";
import { ArtifactSection } from "./ArtifactSection";
import { CodeSection } from "./CodeSection";
import { LaneV2TaskSection } from "./LaneV2TaskSection";
import { getArtifact } from "@/services/multi-agent/api";
import {
  selectWorkspaceOpenedBy,
  selectWorkspacePayload,
  selectWorkspaceRef,
  selectWorkspaceSection,
  useWeaveWorkspaceStore,
  type WorkspaceSection,
} from "@/stores/weaveWorkspaceStore";

/**
 * The inspection surface — everything that used to be stacked above the conversation.
 *
 * ONE SECTION AT A TIME, and that is the design rather than a limitation. The problem being fixed
 * was eleven protocol panels in one column at equal visual weight; a workspace with all of them
 * open would move the same stack to the right-hand side and call it progressive disclosure. The
 * store holds a single `section`, so this can only ever show one.
 *
 * IT DOES NOT OWN ANY CONTENT. Every section below is a component that already existed and is
 * mounted unchanged — `ArtifactSection`, `CodeSection`, `LaneV2TaskSection`. The
 * work here is hosting and chrome, not a second implementation of anything.
 *
 * COLUMN ON DESKTOP, SHEET ON NARROW. This is page chrome that genuinely tracks the window — the
 * two-surface split itself — which is the one case §8.1's table assigns to viewport prefixes rather
 * than container measurement. Below `lg` there is no room for two surfaces at once, so the
 * workspace becomes an overlay the reader dismisses to get back to the conversation.
 */
const SECTION_META: Record<WorkspaceSection, { title: string; icon: typeof FileText }> = {
  artifact: { title: "Document", icon: FileText },
  code: { title: "Code", icon: FileCode2 },
  task: { title: "Changes", icon: ListChecks },
};

export interface WeaveWorkspaceProps {
  /**
   * Write an edit back to a composer attachment — see `CodeSection`. Passed through untouched:
   * the workspace hosts sections, it does not own attachment state (§5), so the route supplies
   * this and a host without a composer (the QA harness) simply omits it.
   */
  onEditAttachment?: (attachmentId: string, content: string) => void;
  className?: string;
}

/** The section body. Extracted so the column and the sheet render identical content. */
function WorkspaceBody({ section, onEditAttachment }: { section: WorkspaceSection } & WeaveWorkspaceProps) {
  const focusRef = useWeaveWorkspaceStore(selectWorkspaceRef);
  const payload = useWeaveWorkspaceStore(selectWorkspacePayload);
  switch (section) {
    case "artifact":
      return <ArtifactSection artifactId={focusRef} fetchArtifact={getArtifact} />;
    case "code":
      // The content came from the click that opened this — `part.body.text`, already in the
      // browser. Nothing is fetched (§3).
      return <CodeSection payload={payload} onEditAttachment={onEditAttachment} />;
    case "task":
      // A lane-v2 task, by id: the R3 snapshot the session store keeps current. Nothing here reads
      // `runStateStore` — a session has no run.
      return <LaneV2TaskSection taskId={focusRef} />;
    default:
      return null;
  }
}

export function WeaveWorkspace({ onEditAttachment, className }: WeaveWorkspaceProps) {
  const section = useWeaveWorkspaceStore(selectWorkspaceSection);
  const openedBy = useWeaveWorkspaceStore(selectWorkspaceOpenedBy);
  const payload = useWeaveWorkspaceStore(selectWorkspacePayload);
  const close = useWeaveWorkspaceStore((s) => s.close);

  /*
   * Focus moves INTO the workspace when it opens, and the heading is the target.
   *
   * A reader who activates "Review plan" with the keyboard and is only shown the panel has been
   * given nothing — their next keystroke still belongs to the transcript. Same rule the interaction
   * card's heading follows.
   */
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  /*
   * …AND BACK OUT AGAIN WHEN IT CLOSES.
   *
   * Moving focus in without ever returning it strands a keyboard reader: the panel disappears and
   * `document.activeElement` falls back to `<body>`, so the next Tab restarts at the top of the
   * page instead of continuing from the card they were reading. The opener is where they were, so
   * the opener is where they go back to.
   *
   * Re-captured on every section change, and only from OUTSIDE the workspace — otherwise switching
   * from the plan to the deployment would record the panel's own heading as the place to return to.
   */
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (section) {
      // An auto-open (changeset ready) must not steal focus from the composer the reader may be typing in.
      // Re-runs when openedBy flips auto -> user on the same section (the reader clicked the opener).
      if (openedBy === "auto") return;
      const active = document.activeElement;
      if (active instanceof HTMLElement && !active.closest("aside, [role='dialog']")) {
        returnFocusRef.current = active;
      }
      headingRef.current?.focus();
      return;
    }
    const opener = returnFocusRef.current;
    returnFocusRef.current = null;
    // `isConnected` because the opener can be gone: a card the stream replaced, or a turn that
    // re-rendered. Focusing a detached node throws focus to `<body>`, which is what this avoids.
    if (opener?.isConnected) opener.focus();
  }, [section, openedBy]);

  /*
   * Escape closes it, from anywhere.
   *
   * The column is not a modal — it does not trap focus and the conversation stays usable — but
   * Escape is what a reader will try, and on the narrow sheet Radix already does it. Doing it here
   * too means the gesture works at both widths, which is what makes it learnable.
   */
  useEffect(() => {
    if (!section) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [section, close]);

  // Track the breakpoint once rather than rendering both trees: mounting the column and the sheet
  // together would mount every section's component twice, and `ChangesetPane` lazily loads Monaco.
  const isWide = useIsWide();

  if (!section) return null;
  const meta = SECTION_META[section];
  const Icon = meta.icon;
  /*
   * The code section wears the BLOCK's name when it has one.
   *
   * A header reading "Code" beside an editor showing `OpportunityApprovalService.cls` wastes the
   * one line the reader uses to confirm they opened the right thing. Falls back to the section
   * title when the block named nothing, which `codePreview` refuses to invent.
   */
  const title = section === "code" ? payload?.title ?? payload?.language ?? meta.title : meta.title;

  /**
   * `withClose` is false on the sheet, because `SheetContent` renders its OWN close button.
   *
   * Rendering both put two X controls on top of each other in the top-right corner at narrow
   * widths — visibly doubled, two tab stops, and two accessible names for one action. Radix's is
   * kept there rather than suppressed: it is the one a reader of any other sheet in this app will
   * already know, and it is positioned by the primitive.
   */
  const headerFor = (withClose: boolean) => (
    <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
      <Icon aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground outline-none"
      >
        {title}
      </h2>
      {withClose && (
        <IconButton type="button" aria-label="Close details" title="Close details" onClick={close}>
          <X className="h-3.5 w-3.5" />
        </IconButton>
      )}
    </div>
  );

  if (!isWide) {
    return (
      <Sheet open onOpenChange={(open) => !open && close()}>
        {/*
          `ma-route` on the content: Radix portals to `<body>`, outside the route class, so the
          multi-agent tokens would silently not apply. `sm:max-w-sm` in the primitive is too narrow
          for a changeset board, so the width is overridden here.
        */}
        <SheetContent side="right" className="ma-route flex w-full flex-col gap-0 p-0 sm:max-w-xl">
          {/* Radix requires a title for the dialog's accessible name; ours is in the header. */}
          <SheetTitle className="sr-only">{title}</SheetTitle>
          {/* No close of our own: `SheetContent` renders one. See `headerFor`. */}
          {headerFor(false)}
          <div className="ma-ws-body min-h-0 flex-1 overflow-y-auto p-3">
            <WorkspaceBody section={section} onEditAttachment={onEditAttachment} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <>
      <aside
        aria-label={`${title} details`}
        className={cn(
          /*
           * `overflow-hidden` + `min-w-0`, MATCHING THE EDITOR PANEL'S OWN OUTER WRAPPER EXACTLY
           * (`MultiAgentMetadataEditorPanel.tsx`: `"flex h-full min-h-0 shrink-0 flex-col
           * overflow-hidden"`, `style={{ width: panelWidth }}`) — the same `useResizeDrag` shape,
           * so the two panels should contain their content the same way too.
           *
           * `min-w-0`: a `flex-shrink: 0` item with an explicit pixel `width` is NOT itself pushed
           * wider by its content — verified directly (a flex row with `overflow: hidden` and a
           * `flex: 0 0 auto; width: 480px` child holding an unbroken 600px-wide string still
           * measures the child at exactly 480px, every time). What `min-w-0` guards here is the
           * SAME class of bug one level down, inside `ma-ws-body` below — see its own comment.
           *
           * `overflow-hidden` is the belt: if anything inside ever DOES compute wider than this
           * fixed width (a future child that isn't as disciplined as `ChangesetPane`'s own
           * `truncate`/Monaco/`FileDiff` scrolling), it is clipped HERE, at the panel's own edge,
           * exactly where the editor panel already clips it — not left to whatever container
           * happens to be the next one up with an `overflow` rule.
           */
          "flex h-full min-h-0 min-w-0 shrink-0 flex-col overflow-hidden border-l border-border bg-background",
          "w-[clamp(360px,34vw,560px)]",
          className,
        )}
      >
        {headerFor(true)}
        {/*
          `ma-ws-body` flattens a lone section's own card — see the token CSS.

          ── WHY `overflow-x-hidden` IS EXPLICIT, NOT LEFT TO DEFAULT ──────────────────────────────

          This div set only `overflow-y-auto`. CSS requires overflow-x and overflow-y to agree on
          "visible or not" — the spec: if one axis is `visible` and the other is not, the UA must
          treat the `visible` one as `auto` instead (verified directly: an element given only
          `overflow-y: auto` reports `overflow-x: auto` from `getComputedStyle`, not `visible`).

          That UNINTENDED `auto` is a real horizontal-scrollbar hazard: this box was only ever meant
          to scroll VERTICALLY (the section list) — every WIDE thing inside it already owns its own
          horizontal handling (`truncate` on file rows, Monaco's own scrollbar in `FileDiff`, the
          workspace's own `CodeSection`/`ArtifactSection`) — so an implicit second scroll axis here
          is not a feature, it is a second place the same overflow could show a scrollbar, keyed off
          nothing more than how close the content happens to sit to the box's exact pixel width.
          `overflow-x-hidden` makes the "one axis only" intent the actual, measured behaviour.
        */}
        <div className="ma-ws-body min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-3">
          <WorkspaceBody section={section} onEditAttachment={onEditAttachment} />
        </div>
      </aside>
    </>
  );
}

/**
 * Is there room for two surfaces side by side?
 *
 * A fourth `useMobile` is exactly what §8.2 forbids, so this uses the same 1024 `lg` boundary
 * Tailwind already defines rather than inventing a number — and it is a WINDOW question, because
 * what it decides is whether the page has two columns at all.
 */
function useIsWide(): boolean {
  const [wide, setWide] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia("(min-width: 1024px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return wide;
}
